import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DollarSign, TrendingUp, AlertTriangle, ShoppingBag, Award, ChevronRight } from "lucide-react";

const BUDGETS = { rapidapi: 25, heygen: 50, openai: 20 };

export default function DashboardPage() {
  const [cost, setCost] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cost/summary?days=30`).then((r) => setCost(r.data)).catch(() => {});
    axios.get(`${API}/creator-rewards/eligibility`).then((r) => setEligibility(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <PageHeader title="Dashboard" subtitle="AffiliatePilot operator console" testid="dashboard-header" />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CostWidget cost={cost} />
          <EligibilityWidget eligibility={eligibility} />
        </div>
        <div className="ap-panel p-5">
          <div className="text-xs text-zinc-500">
            Quick links: <Link to="/app/trends" className="text-emerald-400 hover:text-emerald-300">Trends</Link>
            {" · "}<Link to="/app/marketplace" className="text-emerald-400 hover:text-emerald-300">Marketplace</Link>
            {" · "}<Link to="/app/generate" className="text-emerald-400 hover:text-emerald-300">Generate</Link>
            {" · "}<Link to="/app/library" className="text-emerald-400 hover:text-emerald-300">Library</Link>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function CostWidget({ cost }) {
  if (!cost) return <div className="ap-panel p-5 text-sm text-zinc-500" data-testid="cost-widget-loading">Loading cost…</div>;
  const providers = [
    { id: "rapidapi", label: "RapidAPI", value: cost.rapidapi?.est_cost_usd || 0, budget: BUDGETS.rapidapi, color: "amber" },
    { id: "heygen", label: "HeyGen", value: cost.heygen?.est_cost_usd || 0, budget: BUDGETS.heygen, color: "purple" },
    { id: "openai", label: "OpenAI", value: cost.openai?.est_cost_usd || 0, budget: BUDGETS.openai, color: "emerald" },
  ];
  const total = cost.total_usd || 0;
  const grand = BUDGETS.rapidapi + BUDGETS.heygen + BUDGETS.openai;
  const overBudgetProviders = providers.filter((p) => (p.value / p.budget) > 0.8);
  return (
    <div className="ap-panel p-5 space-y-4" data-testid="cost-widget">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-zinc-400 text-xs"><DollarSign className="h-3.5 w-3.5" /> COST (LAST 30 DAYS)</div>
          <div className="text-3xl font-bold text-zinc-100 mt-1" data-testid="cost-total">${total.toFixed(2)}</div>
          <div className="text-[11px] text-zinc-500">of ${grand} total budget</div>
        </div>
        {overBudgetProviders.length > 0 && (
          <Badge variant="outline" className="border-amber-700/60 bg-amber-500/10 text-amber-300" data-testid="budget-alert">
            <AlertTriangle className="h-3 w-3 mr-1" /> {overBudgetProviders.length} over 80%
          </Badge>
        )}
      </div>

      {/* Stacked horizontal bar */}
      <div className="flex h-2 rounded-full overflow-hidden bg-zinc-900" data-testid="cost-stacked-bar">
        {providers.map((p) => {
          const pct = total > 0 ? (p.value / total) * 100 : 0;
          const bg = { amber: "bg-amber-500", purple: "bg-purple-500", emerald: "bg-emerald-500" }[p.color];
          return <div key={p.id} className={bg} style={{ width: `${pct}%` }} title={`${p.label}: $${p.value.toFixed(2)}`} />;
        })}
      </div>

      {/* Per-provider rows */}
      <div className="space-y-2" data-testid="cost-providers">
        {providers.map((p) => {
          const ratio = p.value / p.budget;
          const fillColor = ratio > 1 ? "bg-rose-500" : ratio > 0.8 ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div key={p.id} className="text-xs" data-testid={`cost-row-${p.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-zinc-300">{p.label}</span>
                <span className="mono text-zinc-400">${p.value.toFixed(2)} / ${p.budget}</span>
              </div>
              <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                <div className={`h-full ${fillColor}`} style={{ width: `${Math.min(ratio * 100, 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EligibilityWidget({ eligibility }) {
  if (!eligibility) return <div className="ap-panel p-5 text-sm text-zinc-500" data-testid="eligibility-widget-loading">Loading eligibility…</div>;

  if (eligibility.source === "no_shop_connected") {
    return (
      <div className="ap-panel p-5 flex flex-col items-center justify-center text-center min-h-[200px]" data-testid="eligibility-empty">
        <ShoppingBag className="h-8 w-8 text-zinc-600 mb-2" />
        <div className="text-sm text-zinc-300 mb-1">Connect a TikTok Shop</div>
        <div className="text-xs text-zinc-500 mb-3">to track Creator Rewards eligibility</div>
        <Link to="/app/settings">
          <Button size="sm" className="bg-pink-500 hover:bg-pink-400 text-zinc-950 font-semibold" data-testid="eligibility-connect-cta">
            Go to Settings <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </div>
    );
  }

  const followers = eligibility.followers || 0;
  const views = eligibility.views_30d || 0;
  const fTarget = eligibility.followers_target || 10000;
  const vTarget = eligibility.views_target_30d || 100000;
  const badges = [
    { id: "bio", label: "Bio Link Unlocked", met: eligibility.bio_link_unlocked },
    { id: "shop", label: "TikTok Shop Affiliate", met: eligibility.tiktok_shop_affiliate_eligible },
    { id: "rewards", label: "Creator Rewards Program", met: eligibility.creator_rewards_eligible },
  ];

  return (
    <div className="ap-panel p-5 space-y-4" data-testid="eligibility-widget">
      <div className="flex items-center gap-2 text-zinc-400 text-xs">
        <Award className="h-3.5 w-3.5" /> CREATOR REWARDS ELIGIBILITY
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div data-testid="eligibility-followers">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs text-zinc-400">Followers</span>
            <span className="mono text-[11px] text-zinc-300">{followers.toLocaleString()} / {fTarget.toLocaleString()}</span>
          </div>
          <Progress value={Math.min((followers / fTarget) * 100, 100)} className="h-2 bg-zinc-900" />
        </div>
        <div data-testid="eligibility-views">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs text-zinc-400">Views (30d)</span>
            <span className="mono text-[11px] text-zinc-300">{views.toLocaleString()} / {vTarget.toLocaleString()}</span>
          </div>
          <Progress value={Math.min((views / vTarget) * 100, 100)} className="h-2 bg-zinc-900" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="eligibility-badges">
        {badges.map((b) => (
          <Badge key={b.id} variant="outline" data-testid={`eligibility-badge-${b.id}`}
                 className={b.met ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[10px]"
                                  : "border-zinc-700 text-zinc-500 text-[10px]"}>
            {b.met ? "✓ " : "○ "}{b.label}
          </Badge>
        ))}
      </div>

      {eligibility.source === "unavailable" && (
        <div className="text-[10px] text-amber-400/80 mono">
          Live shop metrics unavailable — verify scope `data.shop_analytics.public.read` enabled.
        </div>
      )}
    </div>
  );
}
